/**
 * Filter Initializer Composable
 *
 * Hydrates `productListFilters` from a per-field merge of these sources:
 *
 *   forced (`override-user: true` / non-empty `presets`)  // wins unconditionally
 *     sessionStorage (only when fromBackForward = true)   // restore from detail
 *       URL                                                // user's shared link
 *         localStorage                                     // user's previous session
 *           ibeSearchDefaults                              // backend per-page defaults
 *             hardcoded fallback                           // last resort
 *
 * SessionStorage carries only `dateRange`, `duration`, `roomOccupancy` — the
 * three fields the user can edit on OfferPage. It is consulted only when the
 * navigation is detected as Back/Forward; on a fresh load (deep link, ad
 * landing) the URL keeps priority. After hydration from sessionStorage the
 * URL is rewritten via `replaceState` so an F5 reload preserves the values.
 *
 * Forced fields ignore URL + sessionStorage + localStorage in both
 * directions; the SearchBar renders them as disabled and the filter list
 * hides or disables their groups. Forced filter group presets are seeded
 * into a separate ref (`forcedAccommodationProperties`) which merges into
 * `apiParams` but never surfaces in chips, the URL, or localStorage.
 *
 * `hasUserInteracted` flips to `true` only when URL, sessionStorage, or
 * localStorage contributed at least one user-controlled value **for a field
 * whose owning UI is rendered on this page**. Pages that hydrate purely from
 * forced + defaults stay quiet so the URL/LS watchers don't write back.
 *
 * Page-wide UI visibility (`pageSearchBarVisible`, `pageFiltersVisible`) is
 * derived in `main.ts` from `[data-app-embed]` attributes before any mount
 * and read here from the shared store. When SearchBar is hidden, URL/LS/SS
 * are ignored for `dest` / `date` / `todate` / `days` / `pax`. When FilterBar
 * is hidden, URL/LS are ignored for `accommodationProperties` / `sort`.
 * `currentPage` is sourced only from the URL (never localStorage, so it does
 * not leak across SRP pages); it contributes to `hasUserInteracted` only when
 * at least one of SearchBar/FilterBar is visible.
 *
 * Usage:
 * ```ts
 * const { initializeFilters } = useFilterInitializer();
 * onMounted(async () => {
 *   await initializeFilters(isBackForwardNavigation());
 * });
 * ```
 */

import {
  useProductListFiltersStore,
  SortOption,
  type FilterState,
} from '@/stores/productListFilters';
import { isFieldForced, useFilterConfigurationStore } from '@/stores/filterConfiguration';
import { usePlayerAvailableValuesStore } from '@/stores/playerAvailableValues';
import { UrlParameterService, type UrlParameters } from '@/services/url/urlParameterService';
import {
  SessionStorageService,
  type ActiveSearchParams,
} from '@/services/storage/sessionStorageService';
import { loadIbeSearchDefaults } from '@/composables/filters/useSearchDefaults';
import { loadIbeFilterConfiguration } from '@/composables/filters/useFilterConfiguration';
import { FilterValidation } from '@/utils/validation/filterValidation';
import { nightsInRange, resolveDurationIntent } from '@/utils/filters/durationMode';
import { MAX_PRODUCT_DURATION } from '@/utils/filters/durationPlan';
import { sanitizeStoredFilters } from '@/utils/filters/sanitizeStoredFilters';
import {
  parseDatesFromConfig,
  parsePaxConfigString,
  getHardcodedDefaults,
} from '@/utils/searchDefaults/searchDefaultsResolver';
import type { DateRange } from '@/types/SearchBar';
import type { FieldConfig, IbeSearchDefaults } from '@/types/SearchDefaults';

export function useFilterInitializer() {
  const filtersStore = useProductListFiltersStore();
  const configStore = useFilterConfigurationStore();
  const availableValuesStore = usePlayerAvailableValuesStore();

  async function initializeFilters(fromBackForward: boolean = false): Promise<void> {
    await availableValuesStore.fetchAvailableValues();

    const defaults = loadIbeSearchDefaults();
    const filterConfig = loadIbeFilterConfiguration();

    if (filterConfig) configStore.loadFromFilterConfiguration(filterConfig);
    if (defaults) configStore.loadFromDefaults(defaults);

    const showSearchBar = filtersStore.pageSearchBarVisible;
    const showFilters = filtersStore.pageFiltersVisible;

    const urlParams = UrlParameterService.parseUrlParameters();
    const rawLsState = filtersStore.localStorageEnabled
      ? filtersStore.loadFromLocalStorage()
      : null;
    const ls = rawLsState ? sanitizeStoredFilters(rawLsState) : null;

    const ssRaw: ActiveSearchParams | null = fromBackForward ? SessionStorageService.read() : null;
    const ss: ActiveSearchParams | null = showSearchBar ? ssRaw : null;

    let userContributed = false;
    let ssContributed = false;
    const state: FilterState = {};

    // dest — multi-country, normalized to uppercase from URL/LS sources.
    const destResolved = resolveField(
      defaults?.dest,
      urlDest(urlParams),
      undefined,
      ls?.selectedCountries?.map((c) => c.toUpperCase()),
      undefined,
      showSearchBar
    );
    if (destResolved.value !== undefined) state.selectedCountries = destResolved.value;
    if (destResolved.source === 'url' || destResolved.source === 'ls') userContributed = true;

    // date + todate — pair forced together if either has override-user
    const dateForced = isFieldForced(defaults?.date) || isFieldForced(defaults?.todate);
    const urlDateRange: DateRange =
      urlParams.startDate && urlParams.endDate ? [urlParams.startDate, urlParams.endDate] : null;
    const dateRangeResolved = resolveDateRange(
      defaults,
      dateForced,
      ss?.dateRange ?? undefined,
      urlDateRange ?? undefined,
      ls?.dateRange ?? undefined,
      showSearchBar
    );
    if (dateRangeResolved.value !== undefined) state.dateRange = dateRangeResolved.value;
    if (
      dateRangeResolved.source === 'ss' ||
      dateRangeResolved.source === 'url' ||
      dateRangeResolved.source === 'ls'
    ) {
      userContributed = true;
    }
    if (dateRangeResolved.source === 'ss') ssContributed = true;

    // days → duration
    const daysResolved = resolveField(
      defaults?.days,
      urlParams.duration !== undefined ? String(urlParams.duration) : undefined,
      ss?.duration != null ? String(ss.duration) : undefined,
      ls?.duration !== undefined ? String(ls.duration) : undefined,
      undefined,
      showSearchBar
    );
    if (daysResolved.value !== undefined) {
      const validated = FilterValidation.validateDuration(daysResolved.value);
      if (validated !== null) state.duration = validated;
    }
    if (
      daysResolved.source === 'ss' ||
      daysResolved.source === 'url' ||
      daysResolved.source === 'ls'
    ) {
      userContributed = true;
    }
    if (daysResolved.source === 'ss') ssContributed = true;

    state.durationMode = resolveDurationIntent(urlParams.durationMode, ssRaw?.durationMode);
    if (urlParams.durationMode !== undefined) {
      userContributed = true;
    } else if (ssRaw?.durationMode != null) {
      userContributed = true;
      ssContributed = true;
    }

    // pax → roomOccupancy. Forced collapses to a single room
    const paxForced = isFieldForced(defaults?.pax);
    const paxConfigParsed = defaults?.pax?.value
      ? parsePaxConfigString(defaults.pax.value)
      : undefined;
    if (paxForced) {
      if (paxConfigParsed) state.roomOccupancy = paxConfigParsed;
    } else if (showSearchBar && ss?.roomOccupancy) {
      state.roomOccupancy = ss.roomOccupancy;
      userContributed = true;
      ssContributed = true;
    } else if (showSearchBar && urlParams.roomOccupancy) {
      state.roomOccupancy = urlParams.roomOccupancy;
      userContributed = true;
    } else if (showSearchBar && ls?.roomOccupancy) {
      state.roomOccupancy = ls.roomOccupancy;
      userContributed = true;
    } else if (paxConfigParsed) {
      state.roomOccupancy = paxConfigParsed;
    }

    // sort — flat, no override-user; URL > LS > default. Owned by FilterBar.
    const sortFromUrl = showFilters ? urlParams.sortBy : undefined;
    const sortFromLs = showFilters ? ls?.sortBy : undefined;
    const sortResolved =
      sortFromUrl ?? sortFromLs ?? mapDefaultsSort(defaults) ?? SortOption.RECOMMENDATION;
    state.sortBy = mapSortToOption(sortResolved);
    if (sortFromUrl !== undefined || sortFromLs !== undefined) userContributed = true;

    // fixed-bookable filter — flat, URL > LS > default(false). Owned by FilterBar.
    const fixedFromUrl = showFilters ? urlParams.fixedBookableOnly : undefined;
    const fixedFromLs = showFilters ? ls?.fixedBookableOnly : undefined;
    state.fixedBookableOnly = fixedFromUrl ?? fixedFromLs ?? false;
    if (fixedFromUrl !== undefined || fixedFromLs !== undefined) userContributed = true;

    // page — sourced ONLY from URL; never persisted across pages/sessions.
    // Contributes to hasUserInteracted only when at least one user-editable UI
    // (SearchBar or FilterBar) is visible.
    const pageHasOwnerUi = showSearchBar || showFilters;
    if (urlParams.page !== undefined) {
      state.currentPage = urlParams.page;
      if (pageHasOwnerUi) userContributed = true;
    }
    if (ls?.itemsPerPage !== undefined) state.itemsPerPage = ls.itemsPerPage;

    // accommodation filters — user-controlled keys (URL > LS) for editable groups.
    const urlAttributes = showFilters ? urlParams.attributes : undefined;
    const lsAccommodationProperties = showFilters ? ls?.selectedAccommodationProperties : undefined;
    const urlKeys = urlAttributes ? configStore.resolveCompositeKeysForTokens(urlAttributes) : [];
    if (urlKeys.length > 0) {
      state.selectedAccommodationProperties = urlKeys;
      userContributed = true;
    } else if (lsAccommodationProperties && lsAccommodationProperties.length > 0) {
      state.selectedAccommodationProperties = lsAccommodationProperties;
      userContributed = true;
    } else {
      state.selectedAccommodationProperties = [];
    }

    // Hardcoded final fallback for date range / room occupancy / duration when
    // nothing was resolved from any source above.
    const fallback = getHardcodedDefaults();
    if (state.dateRange === undefined) state.dateRange = fallback.dateRange;
    if (state.roomOccupancy === undefined) state.roomOccupancy = fallback.roomOccupancy;
    if (state.duration === undefined) {
      const nights = nightsInRange(state.dateRange);
      state.duration = nights > 0 ? Math.min(fallback.duration, nights) : fallback.duration;
    }

    if (state.durationMode === 'exact' && nightsInRange(state.dateRange) > MAX_PRODUCT_DURATION) {
      state.durationMode = 'preset';
    }

    // Forced accommodation filter presets
    const apiAvailable = new Set(availableValuesStore.accommodationProperties);
    const validForcedKeys: string[] = [];
    for (const key of configStore.forcedCompositeKeys) {
      if (apiAvailable.has(key)) {
        validForcedKeys.push(key);
      } else {
        console.warn(
          `[useFilterInitializer] Forced preset key "${key}" not in API accommodationProperties — dropping`
        );
      }
    }
    filtersStore.setForcedAccommodationProperties(validForcedKeys);

    filtersStore.setInitialState(state);
    filtersStore.hasUserInteracted = userContributed;

    if (ssContributed) {
      filtersStore.syncToUrl();
    }
  }

  async function resetFiltersToDefaults(): Promise<void> {
    SessionStorageService.clear();
    filtersStore.clearPersistedFilters();
    UrlParameterService.updateUrlParameters({});
    filtersStore.hasUserInteracted = false;

    await initializeFilters(false);
  }

  return {
    initializeFilters,
    resetFiltersToDefaults,
  };
}

type ResolvedSource = 'forced' | 'ss' | 'url' | 'ls' | 'default' | 'none';

interface Resolved<T> {
  value: T | undefined;
  source: ResolvedSource;
}

function resolveField<T>(
  cfg: FieldConfig<T> | undefined,
  fromUrl: T | undefined,
  fromSs: T | undefined,
  fromLs: T | undefined,
  fallback: T | undefined,
  userSourcesEnabled: boolean = true
): Resolved<T> {
  if (cfg?.['override-user'] === true) {
    return { value: cfg.value, source: 'forced' };
  }
  if (userSourcesEnabled) {
    if (fromSs !== undefined) return { value: fromSs, source: 'ss' };
    if (fromUrl !== undefined) return { value: fromUrl, source: 'url' };
    if (fromLs !== undefined) return { value: fromLs, source: 'ls' };
  }
  if (cfg?.value !== undefined) return { value: cfg.value, source: 'default' };
  if (fallback !== undefined) return { value: fallback, source: 'default' };
  return { value: undefined, source: 'none' };
}

function urlDest(urlParams: UrlParameters): string[] | undefined {
  if (!urlParams.destinationId) return undefined;
  const arr = Array.isArray(urlParams.destinationId)
    ? urlParams.destinationId
    : [urlParams.destinationId];
  return arr.map((c) => c.toUpperCase());
}

function resolveDateRange(
  defaults: IbeSearchDefaults | null,
  forced: boolean,
  fromSs: DateRange | undefined,
  fromUrl: DateRange | undefined,
  fromLs: DateRange | undefined,
  userSourcesEnabled: boolean = true
): Resolved<DateRange> {
  const configDates = parseDatesFromConfig(defaults);
  if (forced) {
    if (configDates) return { value: configDates, source: 'forced' };
    return { value: undefined, source: 'none' };
  }
  if (userSourcesEnabled) {
    if (fromSs) return { value: fromSs, source: 'ss' };
    if (fromUrl) return { value: fromUrl, source: 'url' };
    if (fromLs) return { value: fromLs, source: 'ls' };
  }
  if (configDates) return { value: configDates, source: 'default' };
  return { value: undefined, source: 'none' };
}

function mapDefaultsSort(defaults: IbeSearchDefaults | null): SortOption | undefined {
  if (!defaults?.sort) return undefined;
  return defaults.sort === 'price' ? SortOption.PRICE_ASC : SortOption.RECOMMENDATION;
}

function mapSortToOption(value: SortOption | 'price' | 'recommendation'): SortOption {
  if (value === 'price') return SortOption.PRICE_ASC;
  if (value === 'recommendation') return SortOption.RECOMMENDATION;
  return value;
}
